import React from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion, Button, Card, ContentCard, HeaderCard, Icon, Message } from '@weezevent/nacre';

import { useFetchFile } from './hooks/useFetchFile.js';
import { getModerationAttachmentsUrl } from '../../../../../components/upload/utils.js';

import { ModerationProfileSameRequester, ModerationProfileAttachmentsFullscreenModal, ModerationCommentBlock } from './components/exports.js';
import LoadingCard from '../../../../../components/loadingCard.js';

import Style from '../moderationProfile.module.css';

export function ModerationProfileRightContainer({ prefix, attendee, organization, order }) {
    const fieldsWithFileType = React.useMemo(() => {
        return attendee.form?.filter(e => e.field_type === 'file') ?? [];
    }, [attendee]);

    return (
        <div>
            <ModerationProfileSameRequester attendee={attendee} prefix={prefix} order={order} />
            <ModerationProfileAttachments display={fieldsWithFileType?.filter(field => field.answer).length > 0} fieldsWithFileType={fieldsWithFileType} />
            <ModerationCommentBlock organization={organization} attendee={attendee} />
        </div>
    );
}

const ModerationProfileAttachments = ({ display, fieldsWithFileType }) => {
    const { t } = useTranslation();

    if (!display) {
        return null;
    }

    return (
        <Card className={Style['attendee-card']}>
            <HeaderCard>
                <p>{t('ticket.moderation.attendee.attachments.title')}</p>
            </HeaderCard>
            <ContentCard>
                {fieldsWithFileType
                    .filter(field => field.answer)
                    .sort((a, b) => a.order - b.order)
                    .map((field, i) => (
                        <FilePreview key={i} index={i} filePath={field.answer} title={field.question} />
                    ))}
            </ContentCard>
        </Card>
    );
};

const FilePreview = ({ filePath, title, index }) => {
    const { t } = useTranslation();
    const [isHidden, setIsHidden] = React.useState(false);
    const [openFullscreenModal, setOpenFullscreenModal] = React.useState(false);
    const [weezUrl] = React.useState(() => getModerationAttachmentsUrl(filePath, window.weezConfig.aws.weez_bucket_form));
    const { file, extension, loading, error } = useFetchFile({
        path: filePath,
        backupUri: weezUrl,
        launch: filePath.startsWith('http')
    });

    const handleClick = React.useCallback(() => setOpenFullscreenModal(true), []);

    const handleDownload = React.useCallback(() => {
        if (!file || !!error) {
            return;
        }
        window.open(filePath.startsWith('http') ? filePath : weezUrl, '_blank');
    }, [file, filePath, weezUrl]);

    return (
        <div className={Style['attendee-attachments-accordion']}>
            <Accordion
                title={
                    <div className={Style['attendee-attachments-cardContainer']}>
                        <p>{title}</p>
                        <div className={Style['attendee-attachments-cardHideAndShow']} onClick={() => setIsHidden(!isHidden)}>
                            <span>{t(`ticket.common.action.${isHidden ? 'show' : 'hide'}`)}</span>
                            <Icon name={isHidden ? 'eye' : 'eye-slash'} />
                        </div>
                    </div>
                }
                open={!isHidden}
                openByDefault={isHidden}
                onChangeOpen={() => setIsHidden(!isHidden)}
            >
                <>
                    <AttachmentPreview index={index} file={file} loading={loading} error={error} extension={extension} />
                    <div className={Style['attendee-attachments-ctasContainer']}>
                        <Button label={t('ticket.common.action.download')} primary inverted onClick={handleDownload} disabled={!!error} />
                        <Button label={t('ticket.common.action.fullScreen')} icon={<Icon name="area" />} onClick={handleClick} disabled={!!error} />
                    </div>
                </>
            </Accordion>
            {openFullscreenModal && (
                <ModerationProfileAttachmentsFullscreenModal
                    index={index}
                    open={openFullscreenModal}
                    setOpenModal={setOpenFullscreenModal}
                    file={file}
                    extension={extension}
                    title={title}
                />
            )}
        </div>
    );
};

const AttachmentPreview = ({ index, loading, error, file, extension }) => {
    const { t } = useTranslation();

    if (loading) {
        return <LoadingCard loading={loading} />;
    }

    if (error) {
        return (
            <Message title={t(`ticket.moderation.attendee.attachments.fileRejectedTitle`)} type="danger">
                {error}
            </Message>
        );
    }
    return (
        <div className={Style['attendee-attachments-securedContainer']}>
            {extension === 'pdf' ? (
                <div className={Style['attendee-attachments-pdf']}>
                    <iframe
                        title={`pdf_attachment_container_${index}`}
                        className={Style['attendee-attachments-pdf-view']}
                        type="application/pdf"
                        src={`${file}#toolbar=0&navpanes=0`}
                    />
                </div>
            ) : (
                <img className={Style['attendee-attachments-img']} src={file} alt="Attachment preview" />
            )}
        </div>
    );
};
